/**
 * @fileoverview Detect when a deployed asset manifest differs from the bundle loaded by this tab. `vite.config.mjs` owns the versioned entry and manifest contract. Visibility and focus checks keep long-lived staff sessions aware of releases; the user chooses when to refresh so deployment does not interrupt current work. `SidebarLayout.js` places the notice in the account footer and a compact mobile-header action; this component never floats over workspace content.
 */
import React, { useEffect, useState } from 'react';
import AppIcon from './shared/AppIcon';
import './FrontendUpdateNotice.css';

const CHECK_INTERVAL_MS = 60_000;
const REQUEST_TIMEOUT_MS = 10_000;

function assetPath(value) {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value, window.location.href);
    return url.origin === window.location.origin ? url.pathname : null;
  } catch (_error) {
    return null;
  }
}

function loadedAssets() {
  const assets = {};
  document.querySelectorAll('script[src], link[rel="stylesheet"][href]').forEach(element => {
    const path = assetPath(element.getAttribute('src') || element.getAttribute('href'));
    const match = path?.match(/\/static\/(?:js|css)\/main\.[a-f0-9]+\.(js|css)$/);
    if (match) assets[`main.${match[1]}`] = path;
  });
  return assets;
}

export function useFrontendUpdate() {
  // Compare against what this tab actually loaded, even if a deployment
  // finished before the first check. Development bundles are not versioned.
  const [loaded] = useState(loadedAssets);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!loaded['main.js']) return undefined;
    let disposed = false;
    let controller = null;
    let timeout = null;
    let lastCheck = -Infinity;

    const check = async () => {
      if (document.visibilityState === 'hidden' || navigator.onLine === false || controller) return;
      if (Date.now() - lastCheck < 5000) return;
      lastCheck = Date.now();
      controller = new AbortController();
      timeout = window.setTimeout(() => controller?.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch('/asset-manifest.json', {
          cache: 'no-store',
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) return;
        const manifest = await response.json();
        const current = Object.fromEntries(Object.keys(loaded).map(key => [key, assetPath(manifest?.files?.[key])]));
        // Ignore unavailable/partial manifests during a deployment.
        if (Object.values(current).some(path => !path || !/\/static\/(?:js|css)\/main\.[a-f0-9]+\.(js|css)$/.test(path))) return;
        if (!disposed) setAvailable(Object.keys(loaded).some(key => loaded[key] !== current[key]));
      } catch (_error) {
        // Offline or deploying: keep the tab usable and try again later.
      } finally {
        window.clearTimeout(timeout);
        controller = null;
      }
    };

    check();
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    window.addEventListener('online', check);
    window.addEventListener('pageshow', check);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      window.clearTimeout(timeout);
      controller?.abort();
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
      window.removeEventListener('online', check);
      window.removeEventListener('pageshow', check);
    };
  }, [loaded]);

  return available;
}

export default function FrontendUpdateNotice({ available, compact = false, onReload = () => window.location.reload() }) {
  if (!available) return null;

  return (
    <div className={`kc-frontend-update${compact ? ' kc-frontend-update--compact' : ''}`} role="status">
      <button
        type="button"
        title={compact ? 'Update available — save your work, then reload the latest version.' : 'Save your work, then reload the latest version.'}
        onClick={onReload}
      >
        <AppIcon icon="pi pi-refresh" className="kc-frontend-update-icon" aria-hidden="true" />
        <span className="kc-frontend-update-label">Update available</span>
        <span className="kc-frontend-update-dot" aria-hidden="true" />
      </button>
    </div>
  );
}
